/**
 * The T-Lián phone apps (Capacitor, folder mobile/) load this same web app. Here the page talks to the
 * phone: real push notifications (Apple/Google), the number on the app icon, status-bar colours that
 * follow light/dark, the iPhone keyboard, a light tap when sending, Android's back button, and
 * "save/share" for PDFs. In a normal browser `isNative` is false and nothing here runs; the plugins
 * are only loaded inside the app.
 */
import { Capacitor } from '@capacitor/core';

export const isNative = Capacitor.isNativePlatform();
export const nativePlatform = Capacitor.getPlatform() as 'ios' | 'android' | 'web';

type Api = <T = unknown>(method: string, url: string, body?: unknown) => Promise<T>;

let pushToken: string | null = null;

export async function initNative(hooks: { back: () => boolean; openUrl: (url: string) => void }): Promise<void> {
  if (!isNative) return;
  document.documentElement.classList.add('native', `native-${nativePlatform}`);

  const [{ App }, { StatusBar, Style }, { Keyboard }] = await Promise.all([import('@capacitor/app'), import('@capacitor/status-bar'), import('@capacitor/keyboard')]);

  // Status bar follows the app theme (dark text on light, light text on dark).
  const applyBar = async () => {
    const dark = document.documentElement.dataset.theme === 'dark' || (!document.documentElement.dataset.theme && matchMedia('(prefers-color-scheme: dark)').matches);
    await StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }).catch(() => {});
    if (nativePlatform === 'android') {
      const color = getComputedStyle(document.body).backgroundColor || (dark ? '#0b1220' : '#ffffff');
      await StatusBar.setBackgroundColor({ color: toHex(color) }).catch(() => {});
    }
  };
  void applyBar();
  window.addEventListener('tc-theme', () => void applyBar());

  // iPhone: no "‹ › Done" bar above the keyboard; keep the message field in view while typing.
  if (nativePlatform === 'ios') await Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => {});
  Keyboard.addListener('keyboardDidShow', () => {
    const el = document.activeElement as HTMLElement | null;
    el?.scrollIntoView?.({ block: 'nearest' });
    window.dispatchEvent(new Event('tc-keyboard'));
  });

  // Android back button: close what is open, step back from a chat, otherwise leave the app in the background.
  App.addListener('backButton', () => {
    if (!hooks.back()) void App.minimizeApp();
  });

  // Coming back to the app: the chat catches up by itself; clear stale notifications for what is read.
  App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) window.dispatchEvent(new Event('tc-resume'));
  });

  // A tapped notification opens its chat (also when the tap started the app).
  window.addEventListener('tc-open-url', (e) => hooks.openUrl((e as CustomEvent<string>).detail));
  const { PushNotifications } = await import('@capacitor/push-notifications');
  PushNotifications.addListener('pushNotificationActionPerformed', onNotificationTap);
}

function onNotificationTap(a: { notification: { data?: unknown } }): void {
  const url = (a.notification.data as { url?: string } | undefined)?.url;
  if (url && url.startsWith('/')) window.dispatchEvent(new CustomEvent('tc-open-url', { detail: url }));
}

/** Ask for notification permission once signed in, and give the server this phone's push address. */
export async function registerNativePush(api: Api): Promise<void> {
  // Android: only builds made with Firebase (google-services.json) say so in the user agent; without it
  // the push plugin would crash. Firebase does not reach phones in mainland China.
  if (!isNative) return;
  if (nativePlatform === 'android' && !navigator.userAgent.includes('TLianPush/')) return;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  if (nativePlatform === 'android') {
    // One channel for chat messages: sound, heads-up banner, on the lock screen without the text.
    await PushNotifications.createChannel({ id: 'messages', name: 'Messages · 消息 · Meddelanden', importance: 4, visibility: 0, sound: 'default', vibration: true, lights: true }).catch(() => {});
  }
  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') return;
  await PushNotifications.removeAllListeners().catch(() => {});
  PushNotifications.addListener('registration', ({ value }) => {
    pushToken = value;
    void api('POST', '/api/push/native', { platform: nativePlatform, token: value }).catch(() => {});
  });
  PushNotifications.addListener('registrationError', () => {});
  PushNotifications.addListener('pushNotificationActionPerformed', onNotificationTap);
  await PushNotifications.register();
}

/** On sign-out this phone stops getting notifications for the account. */
export async function unregisterNativePush(api: Api): Promise<void> {
  if (!isNative || !pushToken) return;
  await api('POST', '/api/push/native/remove', { token: pushToken }).catch(() => {});
  pushToken = null;
}

/** Number on the app icon. */
export function setNativeBadge(count: number): void {
  if (!isNative) return;
  void import('@capawesome/capacitor-badge').then(({ Badge }) => (count > 0 ? Badge.set({ count }) : Badge.clear())).catch(() => {});
}

/** A light tap when a message is sent. */
export function sendFeedback(): void {
  if (!isNative) return;
  void import('@capacitor/haptics').then(({ Haptics, ImpactStyle }) => Haptics.impact({ style: ImpactStyle.Light })).catch(() => {});
}

/** PDFs: download with the session, then the phone's own share sheet (Save to Files, open in Books, …). */
export async function shareFile(url: string, name: string): Promise<boolean> {
  if (!isNative) return false;
  try {
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) return false;
    const data = await blobToBase64(await res.blob());
    const [{ Filesystem, Directory }, { Share }] = await Promise.all([import('@capacitor/filesystem'), import('@capacitor/share')]);
    const safe = name.replace(/[^\p{L}\p{N}._ -]/gu, '_').slice(0, 120) || 'document.pdf';
    const file = await Filesystem.writeFile({ path: safe, data, directory: Directory.Cache });
    await Share.share({ title: safe, files: [file.uri] });
    return true;
  } catch {
    return false;
  }
}

function blobToBase64(b: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(r.error);
    r.readAsDataURL(b);
  });
}

function toHex(rgb: string): string {
  const m = /rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(rgb);
  if (!m) return rgb.startsWith('#') ? rgb : '#ffffff';
  return '#' + [m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, '0')).join('');
}
